import {
    FilterOperation,
    PagedSortedFilteredRequestParams,
    SortColumn,
    TaskDisplay,
    TaskPriority,
    TaskType,
} from '@centecomm/portal-service';
import { useAdminConfig, useCurrentUser, useUserTasks } from '@centecomm/portal-service-react';
import PecoQualtricsLoader from 'Core/CompanySpecific/PECO/Qualtrics/PecoQualtricsLoader';
import useTaskMapper from 'Features/Tasks/Hooks/useTaskMapper';
import TaskSection from 'Features/Tasks/TaskSection';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Types as TableTypes } from 'Shared/Table';
import useSettingsFromState from 'State/Settings/Hooks/useSettingsFromState';
import useComplianceHubEnabled from 'utils/Hooks/LaunchDarkly/useComplianceHubEnabled';

import DashboardLinks from '../DashboardLinks';
import styles from './dashboard-page.module.css';

export enum DownloadStatus {
    Pending,
    Downloading,
    Downloaded,
}

function DashboardPage(): React.ReactElement {
    const { t } = useTranslation(['dashboardFeature', 'common']);
    const { isLoaded: settingsLoaded, hasError: settingsHaveError, settings } = useSettingsFromState();
    const landingPageSettings = settings?.landingPage;
    const { getLandingPageWelcomeImage } = useAdminConfig();
    const complianceHubEnabled = useComplianceHubEnabled(true);

    const [importantTask, setImportantTask] = useState<TaskDisplay | null>(null);
    const [coiTasks, setCOITasks] = useState<TaskDisplay[]>([]);

    const { getTasks } = useUserTasks();
    const { isOwnerOperator: currentUserIsOwnerOperator } = useCurrentUser();
    const sortColumn: SortColumn[] = [{ columnName: 'Id', sortDirection: TableTypes.SortDirection.DESC }];

    const mapTasks = useTaskMapper(currentUserIsOwnerOperator, complianceHubEnabled);

    const hasCustomWelcomeImage = React.useMemo(() => {
        if (!settingsHaveError && settingsLoaded && landingPageSettings?.welcomeImageKey) {
            return true;
        }

        return false;
    }, [landingPageSettings]);
    const [welcomeImageSrc, setWelcomeImageSrc] = React.useState<string | undefined>();
    const [welcomeImageDownloadStatus, setWelcomeImageDownloadStatus] = React.useState<DownloadStatus | null>(null);

    React.useEffect(() => {
        if (welcomeImageDownloadStatus === null && hasCustomWelcomeImage) {
            setWelcomeImageDownloadStatus(DownloadStatus.Pending);
        }

        if (!hasCustomWelcomeImage) {
            setWelcomeImageDownloadStatus(DownloadStatus.Downloaded);
        }
    }, [hasCustomWelcomeImage, welcomeImageDownloadStatus]);

    React.useLayoutEffect(() => {
        async function fetchImage() {
            try {
                setWelcomeImageDownloadStatus(DownloadStatus.Downloading);
                const imageData = await getLandingPageWelcomeImage(landingPageSettings?.welcomeImageKey as string);
                setWelcomeImageSrc(imageData.signedUrl);
            } catch (error) {
                console.error(error);
                setWelcomeImageSrc(undefined);
            } finally {
                setWelcomeImageDownloadStatus(DownloadStatus.Downloaded);
            }
        }

        if (welcomeImageDownloadStatus === DownloadStatus.Pending && landingPageSettings?.welcomeImageKey) {
            fetchImage();
        }
    }, [welcomeImageDownloadStatus, landingPageSettings]);

    const welcomeTitle = React.useMemo(() => {
        const defaultTitle = t('dashboardFeature:welcome.title');

        if (!settingsLoaded) {
            return null;
        }

        if (settingsHaveError) {
            return defaultTitle;
        }

        return landingPageSettings?.welcomeTitle || defaultTitle;
    }, [landingPageSettings, settingsLoaded, settingsHaveError]);

    const welcomeParagraph = React.useMemo(() => {
        const defaultParagraph = t('dashboardFeature:welcome.paragraph');

        if (!settingsLoaded) {
            return null;
        }

        if (settingsHaveError) {
            return defaultParagraph;
        }

        return landingPageSettings?.welcomeParagraph || defaultParagraph;
    }, [landingPageSettings, settingsLoaded, settingsHaveError]);

    React.useEffect(() => {
        (async () => {
            await getTasks(
                new PagedSortedFilteredRequestParams(
                    sortColumn,
                    [{ columnNames: ['Priority'], value: TaskPriority.Important, filterOperation: FilterOperation.EQUALS }],
                    3,
                    0,
                ),
            )
                .then((response) => response.results)
                .then((tasks) => {
                    const mappedTasks = mapTasks(tasks);

                    // For tenant users who have not completed their leasing application, we want to show that task first.
                    if (mappedTasks.find((item) => item.type === TaskType.CompleteLeasingApplication)) {
                        return mappedTasks.filter((item) => item.type === TaskType.CompleteLeasingApplication);
                    }

                    const tasksFiltered = mappedTasks.filter(
                        (item) => item.type === TaskType.CertificateOfInsuranceNeedsSubmitted,
                    );
                    setCOITasks(tasksFiltered);

                    return mappedTasks;
                })
                .then((task) => setImportantTask(task?.[0]));
        })();
    }, []);

    return (
        <>
            <Helmet>
                <title>{t('common:tenant')}</title>
            </Helmet>
            <div className={styles.DashboardPage}>
                {importantTask ? (
                    <TaskSection task={importantTask} clearImportantTaskReminder={() => setImportantTask(null)} />
                ) : null}
                <div className={styles.BannerImageWrapper}>
                    {welcomeImageDownloadStatus === DownloadStatus.Downloaded && (
                        <picture>
                            <source
                                srcSet={welcomeImageSrc || `${ROOT}assets/idtenant/dashboard-banner/dashboard-header.png`}
                                media="(max-width: 750px)"
                            />
                            <source
                                srcSet={
                                    welcomeImageSrc || `${ROOT}assets/idtenant/dashboard-banner/dashboard-header-med.jpg`
                                }
                                media="(max-width: 1150px)"
                            />
                            <img
                                src={welcomeImageSrc || `${ROOT}assets/idtenant/dashboard-banner/dashboard-header.png`}
                                alt="Tenant Banner"
                                className={styles.BannerImage}
                            />
                        </picture>
                    )}
                    <div className={styles.TopBlock}>
                        <h1>{welcomeTitle}</h1>
                        <h2>{welcomeParagraph}</h2>
                    </div>
                </div>
                <DashboardLinks className={`${styles.QuickLinks} ${styles.QuickLinksMobile}`} />
            </div>
            <div className={styles.QuickLinksContainer}>
                <DashboardLinks className={styles.QuickLinks} />
            </div>
            <PecoQualtricsLoader />
        </>
    );
}

export default DashboardPage;
